import { yupResolver } from '@hookform/resolvers/yup';
import { Add, Cancel, Send } from '@mui/icons-material';
import { Stack, Grid, Box } from '@mui/material';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IConsultant, IJob, ILocation, IUserDTO } from 'wisestep-api';
import { string, object } from 'yup';

import IWorkspaceContext from '../../types/IWorkspaceContext';
import { ControlledTextInput } from '../ControlledTextInput';
import EmailFooter from '../EmailFooter';
import EmailTextBorder from '../EmailTextBorder';
import { Layout } from '../Layout';
import { WsButton } from '../WsButton';
import { WsChip, WsChipVariants, WsChipSize } from '../WsChip';
import { WsEditor, useWsEditor } from '../WsEditor';
import { WsList } from '../WsList';
import WsTypography, { WsTypoColors, WsTypoVariants } from '../WsTypography/WsTypography';

const validationSchema = object().shape({
  subject: string().required('Subject is required'),
  // body: string().required('Body is required'),
});

export interface IVendorEmailFormProps {
  vendorList: string[];
  numOfJobs: number;
  numOfConsultants: number;
  jobsOnly?: boolean;
  consultantsOnly?: boolean;
  openJobs: () => void;
  openConsultants: () => void;
  submitAction: <T>(arg: T) => Promise<void>;
  handleClose: () => void;
  selectedJobs?: IJob[];
  selectedConsultents?: IConsultant[];
  workspaceContext?: IWorkspaceContext;
  userContext?: IUserDTO;
  linkCreation?: (job: IJob) => string;
  disabled?: boolean;
}

const VendorEmailForm = ({
  vendorList,
  numOfJobs,
  numOfConsultants,
  jobsOnly = false,
  consultantsOnly = false,
  openJobs,
  openConsultants,
  submitAction,
  handleClose,
  selectedJobs,
  selectedConsultents,
  workspaceContext,
  userContext,
  linkCreation,
  disabled = false,
}: IVendorEmailFormProps) => {
  // const [disabled, setDisabled] = useState(false);
  const instructions = [
    'Please check your email and phone numbers and add them to the Email if not added already',
    'You can edit the email body and subject line to suit your needs',
    'PRO TIP: You can also copy your previously used email templates from Outlook or Gmail',
  ];

  const getLocation = (location: ILocation) =>
    [location.city, location.state, location.country].filter((x) => x).toString();
  const jobsTemplate = `<div>Hi {vendor_name},
  <p> I am hiring for the following jobs. If you have suitable candidates for these roles please
      email me on ${userContext && userContext.email} or call me on ${
    userContext && userContext.phoneNumber
  }</p>
    <table  style="border: 2px solid black">
    <tbody>
    <tr>
         <th colspan="2">S.No</th>
         <th colspan="6">Job Title&nbsp;&nbsp;</th>
         <th colspan="3">Job Type&nbsp;&nbsp;</th>
         <th colspan="3">Company&nbsp;&nbsp;</th>
         <th colspan="3">Location&nbsp;&nbsp;</th>
         <th colspan="3">Job Details&nbsp;&nbsp;</th>
    </tr>
    ${
      selectedJobs &&
      selectedJobs.flatMap(
        (job, index) => `
    <tr data-ng-repeat="job in vm.selectedJobs">
        <td colspan="2">${index + 1}.</td>
        <td colspan="6">&nbsp;&nbsp; ${job.title ?? 'Not Available'}&nbsp;&nbsp;</td>
        <td colspan="3">&nbsp;&nbsp;${job.jobType ?? 'Not Available'}&nbsp;&nbsp;</td>
        <td colspan="3" >&nbsp;&nbsp;${job.companyName ?? 'Not Available'}&nbsp;&nbsp;</td>
        <td colspan="3">&nbsp;&nbsp;${
          (job.locations && getLocation(job?.locations?.[0])) ?? 'Not Available'
        }
        &nbsp;&nbsp;</td>
        <td>&nbsp;&nbsp;<a href=${
          (linkCreation && linkCreation(job)) ?? 'Not Available'
        }>Apply Here </a>&nbsp;&nbsp;</td>
    </tr>`
      )
    }
    </tbody>
</table>
  <br>
  Regards,<br>
  ${`${userContext?.firstName} ${userContext?.lastName}`},<br>
  ${workspaceContext?.name},<br>
  ${workspaceContext?.userId}.<br>
  </div>
  `;

  const ConsultantTemplate = `<div>Hi {vendor_name},
  <p> I am hiring for the following jobs. If you have suitable candidates for these roles please
      email me on ${userContext && userContext.email} or call me on ${
    userContext && userContext.phoneNumber
  }</p>
    
    <table  style="border: 2px solid black">
    <tbody>
    <tr>
        <th colspan="4">Name</th>
        <th colspan="3">Location&nbsp;&nbsp;</th>
        <th colspan="3">Preferred Locations&nbsp;&nbsp;</th>
        <th colspan="5">Job Title&nbsp;&nbsp;</th>
        <th colspan="3">Experience in Years&nbsp;&nbsp;</th>
        <th colspan="3">Skills&nbsp;&nbsp;</th>
        <th colspan="3">Visa Status&nbsp;&nbsp;</th>
    </tr>
    ${
      selectedConsultents &&
      selectedConsultents.flatMap(
        (consultant) => `
    <tr data-ng-repeat="job in vm.selectedJobs">
        <td colspan="4">${consultant.name ?? 'Not Available'}.</td>
        <td colspan="3">&nbsp;&nbsp; ${
          (consultant.location && getLocation(consultant.location)) ?? 'Not Available'
        }&nbsp;&nbsp;</td>
        <td colspan="3">&nbsp;&nbsp;${
          consultant.preferredLocations &&
          getLocation(consultant.preferredLocations?.[0] ?? 'Not Available')
        }&nbsp;&nbsp;</td>
        <td colspan="5" >&nbsp;&nbsp;${consultant.jobTitle ?? 'Not Available'}&nbsp;&nbsp;</td>
        <td colspan="3">&nbsp;&nbsp;${consultant.totalYearsExperience ?? 'Not Available'}
        &nbsp;&nbsp;</td>
        <td colspan="3">&nbsp;&nbsp;${consultant?.skills.map((f) => f['skill']) ?? 'Not Available'}
        &nbsp;&nbsp;</td>
        <td colspan="3">&nbsp;&nbsp;${consultant.visaStatus ?? 'Not Available'}
        &nbsp;&nbsp;</td>
    </tr>`
      )
    }
    </tbody>
</table>
  <br>
  Regards,<br>
  ${`${userContext?.firstName} ${userContext?.lastName}`},<br>
  ${workspaceContext?.name},<br>
  ${workspaceContext?.userId}.<br>
  <br>
  </div>
  `;
  const VendorEmailDefaultValues = {
    subject: '',
    body: jobsOnly ? jobsTemplate : consultantsOnly ? ConsultantTemplate : '',
  };
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: VendorEmailDefaultValues,
  };
  const methods = useForm(formOptions);
  const editor = useWsEditor(
    jobsOnly ? jobsTemplate : consultantsOnly ? ConsultantTemplate : '',
    (htmlBody) => methods.setValue('body', htmlBody)
  );

  const FormLabel = (label: string) => (
    <Grid xs={2} item>
      <Stack direction={'row'} justifyContent={'space-between'} pr={2}>
        <WsTypography color={WsTypoColors.TERTIARY} bold={true} variant={WsTypoVariants.BODY1}>
          {label}
        </WsTypography>
        <WsTypography color={WsTypoColors.TERTIARY} bold={true} variant={WsTypoVariants.BODY1}>
          :
        </WsTypography>
      </Stack>
    </Grid>
  );

  return (
    <Layout>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(submitAction)}>
            <EmailTextBorder>
              <WsList title="Pre-email sending checklist" listArray={instructions} />
            </EmailTextBorder>
            <EmailTextBorder>
              <Stack direction="row" alignItems={'center'}>
                {FormLabel('To')}
                <Grid item xs={10}>
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                    {vendorList.toString()}
                  </WsTypography>
                </Grid>
              </Stack>
            </EmailTextBorder>
            <EmailTextBorder>
              <Stack direction="row" alignItems={'center'}>
                {FormLabel('Broadcast')}
                <Grid xs={5}>
                  <Stack direction="row" spacing={1}>
                    {!consultantsOnly && (
                      <WsChip
                        variant={WsChipVariants.TERTIARY}
                        size={WsChipSize.SMALL}
                        value={numOfJobs + ' Job(s) Added'}
                      />
                    )}
                    {!jobsOnly && (
                      <WsChip
                        variant={WsChipVariants.TERTIARY}
                        size={WsChipSize.SMALL}
                        value={numOfConsultants + ' Consultant(s) Added'}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={5}>
                  <Stack spacing={1} direction="row" justifyContent={'flex-end'}>
                    {!consultantsOnly && (
                      <WsButton
                        size="small"
                        variant="text"
                        startIcon={<Add fontSize="small" />}
                        onClick={openJobs}
                      >
                        Add Jobs
                      </WsButton>
                    )}
                    {!jobsOnly && (
                      <WsButton
                        size="small"
                        variant="text"
                        startIcon={<Add fontSize="small" />}
                        onClick={openConsultants}
                      >
                        Add Consultants
                      </WsButton>
                    )}
                  </Stack>
                </Grid>
              </Stack>
            </EmailTextBorder>
            <EmailTextBorder>
              <Stack direction="row" alignItems={'center'}>
                {FormLabel('Subject')}
                <Grid xs={10}>
                  <ControlledTextInput
                    name="subject"
                    fullWidth
                    variant="standard"
                    inputProps={{
                      style: {
                        padding: 0,
                      },
                    }}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  />
                </Grid>
              </Stack>
            </EmailTextBorder>
            <Box pb={2}>
              <WsEditor editor={editor} />
            </Box>
            <EmailFooter>
              <Stack direction={'row'} justifyContent={'flex-end'} spacing={2}>
                <WsButton
                  size="large"
                  variant="outlined"
                  onClick={handleClose}
                  startIcon={<Cancel />}
                >
                  Cancel
                </WsButton>
                <WsButton size="large" type="submit" startIcon={<Send />} disabled={disabled}>
                  Send
                </WsButton>
              </Stack>
            </EmailFooter>
          </Box>
        </FormProvider>
      </Grid>
    </Layout>
  );
};

export default VendorEmailForm;
